// Grass recipe (lawn / mown turf) — post-plan addition (2026-08-11).  Immutable built-in root
// (never mutate — extend/fork instead; the wild-grass variant is its own root, Recipe_GrassWild).
//
// STRUCTURE (why these layers, in this order):
//   A lawn is NOT a green fbm.  What makes turf read as turf is a DENSE MASS OF DISCRETE BLADES
//   whose silhouettes overlap, over a DARK THATCH/SOIL bed that shows through only in the gaps.
//   So the recipe follows Recipe_Mulch's proven "same primitive, per-layer params, stacked via
//   heightBlend" pattern (plan §4.4: heightBlend "is not optional" — it is what makes overlapping
//   discrete objects read as overlapping rather than as a cross-fade), with the mulch chip swapped
//   for a narrow, elongated, fully-rotated rounded box: a blade.
//
//   1. thatch  — dark green-brown fbm, COLOUR ONLY.  This is the "between the blades" colour; if
//                it is not clearly darker than the blades, the turf loses all depth and reads as
//                flat felt.
//   2. patch   — a broad `multiply` layer: healthy/dry/mown-direction variation at metres scale.
//                Per the authoring rules a `multiply` ramp is a MULTIPLIER and must be centred on
//                1.0 (a ~0.6-valued albedo-shaped ramp here would double-darken the whole turf).
//   3. blades1..3 — three scatter lattices at 3.0 / 2.2 / 1.7 cm spacing.  Decorrelated by
//                DIFFERENT DENSITIES (so the lattices never align — a seed change alone stacks
//                blades exactly on top of each other) plus different colorJitterSeeds, and given
//                progressively DARKER ramps down the stack (Set3 §3.6.3): the top layer is the
//                sunlit blade tips, the lower two are the shaded blades inside the canopy.  That
//                per-layer colour range is also what keeps the zoomed-out mean converging to one
//                uniform green instead of a 3-tone crosshatch.
//   4. grain   — fine fbm, HEIGHT ONLY: sub-blade surface texture so the canopy is not glassy.
//
// SCALE CONTRACT (all lengths in METRES, layers clearly separated coarse→fine, height magnitudes
// ordered the same way):
//   thatch  ~1.2 m   colour only, heightScale 0 (a metres-scale relief would dominate the height
//                    chain and drive every blade heightBlend weight — see Recipe_Dirt)
//   patch   ~3.0 m   colour only (multiply)
//   blades  ~3.0 / 2.2 / 1.7 cm spacing, ~5 / 3.5 / 2.5 mm relief
//   grain   ~8 mm,   ~0.3 mm — an order of magnitude below the blade relief, so the grain normal
//                    cannot swamp the blade shading
//
// SCATTER DOME-SLOPE BOUND (Recipe_Dirt): keep `height < radiusMin * density`, else the feature's
// slope exceeds 1 and the specular lobe blows out.  blades1: 0.005 < 0.22 * 0.03 = 0.0066. ✓
// blades2: 0.0035 < 0.22 * 0.022 = 0.0048. ✓  blades3: 0.0025 < 0.22 * 0.017 = 0.0037. ✓
//
// HONEST EXPECTATION: "good at mid zoom, synthetic very close up".  A rounded-box SDF blade has no
// taper, no curl and no self-shadowing, so at centimetre zoom this reads as flat green slivers
// rather than 3D grass; the 3-lattice stack buys a complex canopy silhouette, which is what the
// mid/far zoom actually shows.  Real close-up turf needs geometry (or a parallax/shell trick) that
// this single-fragment material stage does not have.
import {MacroModConfig, MaterialLayer, MaterialRecipe, ParamSchema} from "../@MaterialTypes.js";
import {colorRamp} from "../RecipeUtils.js";

export const GRASS_RECIPE_ID = "grass";

/** Shared shape params for the three blade layers (the "same primitive, per-layer params" pattern).
 *  `aspect` 7 is the blade silhouette (long + narrow); `rotJitter` 1 because blades point every
 *  which way.  `placeJitter` stays at the fixed 0.7 default rather than 1.0: Set3 §4.4 — a
 *  full-rotation layer given full-swing placement lets neighbours collide, which snapped the
 *  mulch chip lattice back into visible rows.  `presence` 0.9 is HIGH on purpose (unlike mulch's
 *  0.55): a lawn is dense, so nearly every cell should carry a blade; the lattice is broken here
 *  by the three incommensurate densities + rotation rather than by sparsity. */
const BLADE_SHAPE = {
	shape: 1,          // rounded box — the blade silhouette
	aspect: 7,         // long + narrow
	rotJitter: 1,      // blades lie every which way
	placeJitter: 0.7,  // Set3 §4.4 fixed default (see above)
	presence: 0.9,     // dense canopy
	radiusMin: 0.22,   // keeps the dome-slope bound satisfiable at these reliefs (see header)
	radiusMax: 0.45,   // < 0.5 cell: blades in one row can never fuse into a continuous band
} as const;

export const grassRecipe = new MaterialRecipe({
	id: GRASS_RECIPE_ID,
	name: "Grass",
	roughness: 0.88,
	// A leaf's cuticle IS slightly glossy, unlike dirt/mulch/sand (which all opt out at 0) — but a
	// full lobe on a mown lawn reads as plastic, so keep it faint.
	specStrength: 0.12,

	// Option D — the MACRO MODULATOR (see Recipe_Dirt for the full rationale): a slow map-scale field
	// whose local mean survives wide-zoom averaging, so lawn health/mow variation stays visible far out.
	macroMod: new MacroModConfig({
		wavelength: {ref: "mod_wavelength"}, octaves: {ref: "mod_octaves"},
		lacunarity: {ref: "mod_lacunarity"}, persistence: {ref: "mod_persistence"},
		contrast: {ref: "mod_contrast"}, seedOffset: {ref: "mod_seed"},
	}),

	params: [
		// ── Option D — the macro-mod FBM (same defaults as dirt; the field is per-recipe via mod_seed).
		new ParamSchema({key: "mod_wavelength", label: "[mod] Wavelength (m)", kind: "scalar", min: 300, max: 30000, default: 3000}),
		new ParamSchema({key: "mod_octaves", label: "[mod] Octaves", kind: "scalar", min: 1, max: 8, default: 4}),
		new ParamSchema({key: "mod_lacunarity", label: "[mod] Lacunarity", kind: "scalar", min: 1.1, max: 5, default: 2.6}),
		new ParamSchema({key: "mod_persistence", label: "[mod] Persistence", kind: "scalar", min: 0.1, max: 0.9, default: 0.62}),
		new ParamSchema({key: "mod_contrast", label: "[mod] Contrast", kind: "scalar", min: 0.5, max: 6, default: 2}),
		new ParamSchema({key: "mod_seed", label: "[mod] Seed", kind: "scalar", min: -1000, max: 1000, default: 40}),
		// ── Option D modulation-strength knobs (ALL-ZERO = the pre-Option-D look).
		// Thatch tone: slides the thatch ramp lookup AND its fade target (survives any zoom).
		new ParamSchema({key: "mod_thatchAmount", label: "[mod] Thatch tone swing", kind: "scalar", min: 0, max: 0.4, default: 0.2}),
		// Patch brightness: slides the MULTIPLY ramp lookup (a multiplier near 1.0, so it swings
		// brightness = "lawn health" at the wide zooms where the blades are gone).  No rampMeanShifted (see colorShift).
		new ParamSchema({key: "mod_patchAmount", label: "[mod] Patch brightness swing", kind: "scalar", min: 0, max: 0.3, default: 0.2}),
		// Blade presence: swings the spawn fraction → dense vs sparse canopy patches (a reason for the tone up close).
		new ParamSchema({key: "mod_bladeAmount", label: "[mod] Blade presence swing", kind: "scalar", min: 0, max: 0.9, default: 0.35}),

		new ParamSchema({key: "thatch_scale", label: "Thatch scale (m)", kind: "scalar", min: 0.1, max: 10, default: 1.2}),
		// Deliberately DARK and slightly brown: this is the shadowed soil/dead-leaf layer seen
		// between blades.  Too light here and the turf reads as flat felt (no canopy depth).
		new ParamSchema({key: "thatch_ramp", label: "Thatch ramp", kind: "ramp", default: colorRamp([
			{position: 0,   color_str: `rgb(58,76,37)`},
			{position: 0.5, color_str: `rgb(63,82,39)`},
			{position: 1,   color_str: `rgb(70,87,42)`},
		])}),
		// Broad health/mow variation.  MULTIPLY ramp ⇒ centred on 1.0 (authoring rule: an `over`
		// ramp is an albedo, a `multiply` ramp is a multiplier).  Slightly yellow-warm in the
		// bright tail (sun-stressed patches lose green before they lose luminance).
		new ParamSchema({key: "patch_scale", label: "Patch scale (m)", kind: "scalar", min: 0.3, max: 30, default: 3.0}),
		new ParamSchema({key: "patch_ramp", label: "Patch ramp (multiplier)", kind: "ramp", default: colorRamp([
			{position: 0,   color_str: `rgb(224,209,222)`},
			{position: 0.5, color_str: `rgb(255,255,255)`},
			{position: 1,   color_str: `rgb(255,255,255)`},
		])}),
		new ParamSchema({key: "blade1_density", label: "Blade layer 1 spacing (m)", kind: "scalar", min: 0.005, max: 0.5, default: 0.005}),
		new ParamSchema({key: "blade2_density", label: "Blade layer 2 spacing (m)", kind: "scalar", min: 0.005, max: 0.5, default: 0.02}),
		new ParamSchema({key: "blade3_density", label: "Blade layer 3 spacing (m)", kind: "scalar", min: 0.005, max: 0.5, default: 0.04}),
		// Layer 1 = the sunlit blade tips (the brightest, most saturated greens).  Per-cell colour
		// jitter across this whole span is what gives turf its characteristic fine mottling.
		new ParamSchema({key: "blade1_ramp", label: "Blade layer 1 ramp", kind: "ramp", default: colorRamp([
			{position: 0,    color_str: `rgb(56,81,34)`},
			{position: 0.45, color_str: `rgb(73,98,42)`},
			{position: 1,    color_str: `rgb(97,122,58)`},
		])}),
		// Set3 §3.6.3 — layers 2/3 get their OWN progressively darker ramps (the blades deeper in
		// the canopy), NOT a shared ramp.  Distinct ranges stop the 3-lattice Moiré reading as a
		// 3-tone crosshatch and keep the coarse-zoom mean a single uniform green.
		new ParamSchema({key: "blade2_ramp", label: "Blade layer 2 ramp", kind: "ramp", default: colorRamp([
			{position: 0,    color_str: `rgb(44,66,28)`},
			{position: 0.45, color_str: `rgb(58,80,34)`},
			{position: 1,    color_str: `rgb(77,97,45)`},
		])}),
		new ParamSchema({key: "blade3_ramp", label: "Blade layer 3 ramp", kind: "ramp", default: colorRamp([
			{position: 0,    color_str: `rgb(34,52,22)`},
			{position: 0.45, color_str: `rgb(45,64,28)`},
			{position: 1,    color_str: `rgb(61,78,36)`},
		])}),
		// Reliefs: see the dome-slope bound in the header (height < radiusMin * density).
		new ParamSchema({key: "blade1_height", label: "Blade layer 1 relief (m)", kind: "scalar", min: 0, max: 0.05, default: 0.005}),
		new ParamSchema({key: "blade2_height", label: "Blade layer 2 relief (m)", kind: "scalar", min: 0, max: 0.05, default: 0.005}),
		new ParamSchema({key: "blade3_height", label: "Blade layer 3 relief (m)", kind: "scalar", min: 0, max: 0.05, default: 0.0015}),
		// Small relative to the blade reliefs: a blade is a DISCRETE object and should win or lose
		// crisply (like mulch's chips), unlike dirt's half-buried pebbles.
		new ParamSchema({key: "blade_softness", label: "Blade blend softness", kind: "scalar", min: 0.0001, max: 0.02, default: 0.003}),
		new ParamSchema({key: "grain_scale", label: "Grain scale (m)", kind: "scalar", min: 0.001, max: 0.2, default: 0.13}),
		new ParamSchema({key: "grain_height", label: "Grain relief (m)", kind: "scalar", min: 0, max: 0.01, default: 0.0038}),
	],
	layers: [
		new MaterialLayer({
			id: "thatch",
			primitive: "fbm",
			args: {
				scale: {ref: "thatch_scale"},
				octaves: 4,
				lacunarity: 2,
				persistence: 0.55,
				mode: 3, // turbulence — clumped litter, not smooth cloud
				colorRamp: {ref: "thatch_ramp"},
				heightScale: 0,
				// Option D — the macro THATCH tone shift (see Recipe_Dirt's base): `add` (ramp position in [0,1];
				// base 0 so amount alone is the swing).  Moves the fade TARGET colour (rampMeanShifted) so it works at any zoom.
				colorShift: {base: 0, amount: {ref: "mod_thatchAmount"}, op: "add"},
			},
			blend: "over",
			contributes: "color",
		}),
		new MaterialLayer({
			id: "patch",
			primitive: "fbm",
			args: {
				scale: {ref: "patch_scale"},
				octaves: 3,
				lacunarity: 2,
				persistence: 0.5,
				mode: 0, // plain fbm — broad smooth health variation is exactly the "cloudy" look wanted here
				colorRamp: {ref: "patch_ramp"},
				heightScale: 0,
				// Option D — macro PATCH brightness shift (multiply ramp near 1.0 → swings brightness;
				// survives wider zoom than the blades).  No rampMeanShifted: multiplier slides about the constant mean.
				colorShift: {base: 0, amount: {ref: "mod_patchAmount"}, op: "add"},
			},
			blend: "multiply",
			contributes: "color",
		}),
		// Blade layers 1–3: coarsest/tallest first.  Each heightBlends against the layers below it,
		// so where two blades overlap the taller one's colour wins (plan §4.4) — the canopy read.
		new MaterialLayer({
			id: "blades1",
			primitive: "scatter",
			args: {
				...BLADE_SHAPE,
				density: {ref: "blade1_density"},
				// Option D — macro-modulated blade presence: swings the spawn fraction (dense vs sparse patches);
				// `mul` on the 0.9 base → ~0.58..1.0 at amount 0.35 (a hash threshold, so >1 just means every cell).
				presence: {base: 0.9, amount: {ref: "mod_bladeAmount"}, op: "mul"},
				colorRamp: {ref: "blade1_ramp"},
				height: {ref: "blade1_height"},
				colorJitterSeed: 401,
			},
			blend: "heightBlend",
			blendArgs: {softness: {ref: "blade_softness"}},
			contributes: "both",
		}),
		new MaterialLayer({
			id: "blades2",
			primitive: "scatter",
			args: {
				...BLADE_SHAPE,
				density: {ref: "blade2_density"},
				presence: {base: 0.9, amount: {ref: "mod_bladeAmount"}, op: "mul"},
				colorRamp: {ref: "blade2_ramp"},
				height: {ref: "blade2_height"},
				colorJitterSeed: 509,
			},
			blend: "heightBlend",
			blendArgs: {softness: {ref: "blade_softness"}},
			contributes: "both",
		}),
		new MaterialLayer({
			id: "blades3",
			primitive: "scatter",
			args: {
				...BLADE_SHAPE,
				density: {ref: "blade3_density"},
				presence: {base: 0.9, amount: {ref: "mod_bladeAmount"}, op: "mul"},
				colorRamp: {ref: "blade3_ramp"},
				height: {ref: "blade3_height"},
				colorJitterSeed: 613,
			},
			blend: "heightBlend",
			blendArgs: {softness: {ref: "blade_softness"}},
			contributes: "both",
		}),
		new MaterialLayer({
			id: "grain",
			primitive: "fbm",
			args: {
				scale: {ref: "grain_scale"},
				octaves: 3,
				lacunarity: 2.4,
				persistence: 0.5,
				mode: 3,
				heightScale: {ref: "grain_height"},
			},
			blend: "over",
			contributes: "height",
		}),
	],
});
